<script setup>
import { computed } from 'vue';
import { useBranding } from '@/branding';

// The dark brand panel on the sign-in and sign-up pages: school, headline,
// what the system does, tagline and address, over an optional campus photo.
// Everything comes from School Settings; School Settings › Sign-in page
// passes unsaved values as props for its live preview.
const props = defineProps({
  headline: { type: String, default: undefined },
  overlay: { type: Number, default: undefined }, // 0–90 (% darkness on the photo)
  showFeatures: { type: Boolean, default: undefined },
  backgroundUrl: { type: String, default: undefined },
  // Smaller type and spacing for the settings preview
  compact: { type: Boolean, default: false },
});

const DEFAULT_HEADLINE = 'Everything your school needs, in one place.';
const FEATURES = [
  { icon: 'mdi-notebook-outline', title: 'Class records', text: 'Grades, transmutation and report cards.' },
  { icon: 'mdi-calendar-check-outline', title: 'Attendance', text: 'Daily attendance with early dropout alerts.' },
  { icon: 'mdi-account-child-outline', title: 'For parents', text: 'Grades, attendance and reports at a glance.' },
  { icon: 'mdi-bullhorn-outline', title: 'School updates', text: 'Announcements and events in one place.' },
];

const branding = useBranding();
const pick = (prop, saved) => (prop === undefined ? saved : prop);

const headline = computed(() => pick(props.headline, branding.loginHeadline) || DEFAULT_HEADLINE);
const showFeatures = computed(() => pick(props.showFeatures, branding.loginShowFeatures) !== false);
const photo = computed(() => {
  if (props.backgroundUrl !== undefined) return props.backgroundUrl;
  const bg = branding.assets['login-background'];
  return bg?.custom ? bg.url : '';
});

// Darker towards the bottom, where the tagline and address sit.
const overlayStyle = computed(() => {
  const o = Math.min(90, Math.max(0, Number(pick(props.overlay, branding.loginOverlay) ?? 60))) / 100;
  const a = (v) => Math.min(0.95, v).toFixed(2);
  return {
    '--photo-overlay': `linear-gradient(to bottom, rgba(9, 9, 11, ${a(o * 0.9)}) 0%, rgba(9, 9, 11, ${a(o * 0.75)}) 40%, rgba(9, 9, 11, ${a(o + 0.3)}) 100%)`,
  };
});

// "Panabo City · Division of Panabo City" — whatever the school has set.
const place = computed(() => [branding.address, branding.division].filter(Boolean).join(' · '));
</script>

<template>
  <aside
    class="auth__brand"
    :class="{ 'auth__brand--photo': photo, 'auth__brand--compact': compact }"
    :style="overlayStyle"
    aria-hidden="true"
  >
    <img v-if="photo" :src="photo" alt="" class="auth__photo" />
    <component :is="compact ? 'div' : 'router-link'" to="/" class="auth__brand-head">
      <span class="auth__brand-mark"><img :src="branding.logoUrl" alt="" /></span>
      <span class="auth__brand-names">
        <span class="auth__brand-short">{{ branding.shortName }}</span>
        <span class="auth__brand-app">Class Management</span>
      </span>
    </component>

    <div class="auth__brand-body">
      <h2 class="auth__headline">{{ headline }}</h2>
      <ul v-if="showFeatures" class="auth__features">
        <li v-for="f in FEATURES" :key="f.title" class="auth__feature">
          <span class="auth__feature-icon"><v-icon :size="compact ? 14 : 18">{{ f.icon }}</v-icon></span>
          <span>
            <span class="auth__feature-title">{{ f.title }}</span>
            <span class="auth__feature-text">{{ f.text }}</span>
          </span>
        </li>
      </ul>
    </div>

    <footer class="auth__brand-foot">
      <blockquote v-if="branding.tagline" class="auth__quote">“{{ branding.tagline }}”</blockquote>
      <p class="auth__school">{{ branding.name }}</p>
      <p v-if="place" class="auth__place">{{ place }}</p>
    </footer>
  </aside>
</template>

<style scoped>
.auth__brand {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 40px;
  padding: 40px 48px;
  overflow: hidden;
  isolation: isolate;
  background: #18181b;
  color: #fafafa;
}
/* Dot grid, fading out towards the bottom */
.auth__brand::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: -1;
  background-image: radial-gradient(rgba(255, 255, 255, 0.09) 1px, transparent 1px);
  background-size: 22px 22px;
  mask-image: linear-gradient(to bottom, #000 30%, transparent 85%);
}
/* School photo: darkened so the text stays readable, with a hint of the
   school colour; the dot grid is dropped over a photo. */
.auth__photo {
  position: absolute;
  inset: 0;
  z-index: -2;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.auth__brand--photo::before {
  background-image: var(--photo-overlay);
  background-size: auto;
  mask-image: none;
}
/* Soft glows in the school colour */
.auth__brand::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: -1;
  background:
    radial-gradient(520px circle at 100% 0%, rgba(var(--brand-rgb), 0.28), transparent 60%),
    radial-gradient(420px circle at 0% 100%, rgba(var(--brand-rgb), 0.18), transparent 60%);
}

.auth__brand-head {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  align-self: flex-start;
  color: inherit;
  text-decoration: none;
}
.auth__brand-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  padding: 6px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.95);
}
.auth__brand-mark img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.auth__brand-names {
  display: flex;
  flex-direction: column;
  line-height: 1.25;
}
.auth__brand-short {
  font-size: 16px;
  font-weight: 600;
}
.auth__brand-app {
  font-size: 13px;
  color: #a1a1aa;
}

.auth__brand-body {
  max-width: 440px;
}
.auth__headline {
  margin: 0 0 28px;
  font-size: 30px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: -0.02em;
}
.auth__features {
  display: grid;
  gap: 20px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.auth__feature {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}
.auth__feature-icon {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.05);
  color: var(--brand);
}
.auth__feature-title {
  display: block;
  font-size: 14px;
  font-weight: 500;
}
.auth__feature-text {
  display: block;
  font-size: 13px;
  color: #a1a1aa;
}

.auth__brand-foot {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}
.auth__quote {
  margin: 0 0 12px;
  font-size: 15px;
  font-style: italic;
  line-height: 1.6;
  color: #e4e4e7;
}
.auth__school {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
}
.auth__place {
  margin: 0;
  font-size: 13px;
  color: #a1a1aa;
}

@media (max-height: 720px) {
  .auth__features {
    gap: 14px;
  }
  .auth__feature-text {
    display: none;
  }
}

/* Settings preview: same panel at about two-thirds scale */
.auth__brand--compact {
  gap: 20px;
  padding: 24px;
}
.auth__brand--compact .auth__brand-mark {
  width: 32px;
  height: 32px;
  padding: 4px;
  border-radius: 8px;
}
.auth__brand--compact .auth__brand-short {
  font-size: 13px;
}
.auth__brand--compact .auth__brand-app,
.auth__brand--compact .auth__feature-text,
.auth__brand--compact .auth__place {
  font-size: 11px;
}
.auth__brand--compact .auth__headline {
  margin-bottom: 16px;
  font-size: 19px;
}
.auth__brand--compact .auth__features {
  gap: 12px;
}
.auth__brand--compact .auth__feature-icon {
  width: 26px;
  height: 26px;
  border-radius: 7px;
}
.auth__brand--compact .auth__feature-title,
.auth__brand--compact .auth__school {
  font-size: 12px;
}
.auth__brand--compact .auth__quote {
  font-size: 12px;
  margin-bottom: 8px;
}
</style>
